/**
 * Item and detail HTML, sanitized to whitelisted tags and attributes (2.G deliverable 2). One
 * linear pass, no regular expression over the input: scripts, styles and embeds are dropped with
 * their content, unknown tags lose their markup but keep their text, every event attribute and
 * every URL whose scheme is not `https:` is dropped, and a stray `<` is escaped. Image paths stay
 * in the text as written; nothing is fetched.
 */
const ALLOWED_TAGS = new Set([
    'a',
    'b',
    'blockquote',
    'br',
    'caption',
    'code',
    'del',
    'details',
    'div',
    'em',
    'figcaption',
    'figure',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'i',
    'img',
    'ins',
    'li',
    'ol',
    'p',
    'pre',
    's',
    'section',
    'span',
    'strong',
    'sub',
    'summary',
    'sup',
    'table',
    'tbody',
    'td',
    'tfoot',
    'th',
    'thead',
    'tr',
    'u',
    'ul',
]);
const DROP_WITH_CONTENT = new Set([
    'applet',
    'embed',
    'frame',
    'frameset',
    'iframe',
    'math',
    'noscript',
    'object',
    'script',
    'style',
    'svg',
    'template',
    'textarea',
    'title',
]);
const VOID_TAGS = new Set(['br', 'hr', 'img']);
const ALLOWED_ATTRS = {
    a: ['href', 'title'],
    img: ['src', 'alt', 'width', 'height', 'title'],
    td: ['colspan', 'rowspan'],
    th: ['colspan', 'rowspan'],
};
const URL_ATTRS = new Set(['href', 'src']);
/** The one scheme a kept URL may name; `isSafeUrl` and the whitelist both read it. */
const URL_SCHEMES = ['https:'];
/**
 * The sanitizer's whitelist, exported with `sanitizeHtml` (DECISIONS 222 ruling 9; the 764
 * addendum) for 2.I-2's biography blocks: the tags kept, the attributes kept on each, the
 * attributes holding a URL (kept only for `https:` or a relative path), and the tags dropped with
 * their content. `sanitizeHtml` reads these same sets; there is no second copy.
 */
export const SANITIZE_WHITELIST = Object.freeze({
    tags: Object.freeze([...ALLOWED_TAGS]),
    attributes: Object.freeze(Object.fromEntries(Object.entries(ALLOWED_ATTRS).map(([t, a]) => [t, Object.freeze([...a])]))),
    urlAttributes: Object.freeze([...URL_ATTRS]),
    urlSchemes: Object.freeze([...URL_SCHEMES]),
    droppedWithContent: Object.freeze([...DROP_WITH_CONTENT]),
});
const isNameChar = (c) => (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || (c >= '0' && c <= '9') || c === '-';
const isSpace = (c) => c === ' ' || c === '\n' || c === '\t' || c === '\r' || c === '\f';
function decodeEntities(v) {
    return v.replace(/&#(x[0-9a-f]{1,6}|\d{1,7});?|&colon;|&tab;|&newline;/gi, (m, code) => {
        if (code === undefined)
            return m.toLowerCase() === '&colon;' ? ':' : '';
        const n = code[0] === 'x' || code[0] === 'X' ? Number.parseInt(code.slice(1), 16) : Number(code);
        return n > 0 && n < 0x110000 ? String.fromCodePoint(n) : '';
    });
}
/**
 * `https:` URLs and relative paths only. A named reference other than `&amp;` (which the decoder
 * does not read, such as `&sol;`) refuses the URL: kept attributes keep their references as
 * written, so the browser would decode what this check never saw.
 */
export function isSafeUrl(raw) {
    const decoded = decodeEntities(raw);
    if (/&(?!amp;)[a-z][a-z0-9]{0,31};/i.test(decoded))
        return false;
    let v = '';
    for (const c of decoded)
        if (c > ' ' && !(c >= '\u007f' && c <= '\u009f'))
            v += c;
    v = v.toLowerCase();
    const colon = v.indexOf(':');
    const cut = Math.min(...['/', '?', '#'].map((c) => (v.includes(c) ? v.indexOf(c) : v.length)));
    if (colon !== -1 && colon < cut)
        return URL_SCHEMES.some((s) => v.startsWith(s));
    return !v.startsWith('//');
}
/**
 * Escapes a kept attribute value. A well-formed character reference (`&amp;`, `&quot;`, `&#39;`,
 * `&#x27;`) is left as written, so a link or tooltip keeps its meaning and sanitizing twice gives
 * the same text; a bare `&` becomes `&amp;`.
 */
const escapeAttr = (v) => v
    .replace(/&(?![a-z][a-z0-9]{0,31};|#\d{1,7};|#x[0-9a-f]{1,6};)/gi, '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
/** Reads a tag starting at `<`; null when the text there is not a tag. */
function readTag(s, start) {
    let i = start + 1;
    const closing = s[i] === '/';
    if (closing)
        i += 1;
    const nameStart = i;
    while (i < s.length && isNameChar(s[i]))
        i += 1;
    if (i === nameStart || !/[a-z]/i.test(s[nameStart]))
        return null;
    const name = s.slice(nameStart, i).toLowerCase();
    const attrs = [];
    while (i < s.length && s[i] !== '>') {
        const c = s[i];
        if (isSpace(c) || c === '/') {
            i += 1;
            continue;
        }
        const aStart = i;
        while (i < s.length && !isSpace(s[i]) && !'/>='.includes(s[i]))
            i += 1;
        const attr = s.slice(aStart, i).toLowerCase();
        while (i < s.length && isSpace(s[i]))
            i += 1;
        let value = '';
        if (s[i] === '=') {
            i += 1;
            while (i < s.length && isSpace(s[i]))
                i += 1;
            const q = s[i];
            if (q === '"' || q === "'") {
                const close = s.indexOf(q, i + 1);
                const stop = close === -1 ? s.length : close;
                value = s.slice(i + 1, stop);
                i = stop + 1;
            }
            else {
                const vStart = i;
                while (i < s.length && !isSpace(s[i]) && s[i] !== '>')
                    i += 1;
                value = s.slice(vStart, i);
            }
        }
        if (attr !== '')
            attrs.push([attr, value]);
    }
    return { name, closing, attrs, end: Math.min(i + 1, s.length) };
}
/**
 * Sanitizes item, detail and biography HTML to `SANITIZE_WHITELIST` (the one sanitizer: the item
 * and detail mappers call it, and 2.I-2 imports it from `@codex/dnd5e/foundry`).
 */
export function sanitizeHtml(input) {
    const lower = input.toLowerCase();
    let out = '';
    let changed = false;
    let i = 0;
    while (i < input.length) {
        const lt = input.indexOf('<', i);
        if (lt === -1) {
            out += input.slice(i);
            break;
        }
        out += input.slice(i, lt);
        if (input.startsWith('<!--', lt)) {
            const close = input.indexOf('-->', lt + 4);
            i = close === -1 ? input.length : close + 3;
            changed = true;
            continue;
        }
        if (input[lt + 1] === '!' || input[lt + 1] === '?') {
            const close = input.indexOf('>', lt);
            i = close === -1 ? input.length : close + 1;
            changed = true;
            continue;
        }
        const tag = readTag(input, lt);
        if (tag === null) {
            out += '&lt;';
            i = lt + 1;
            changed = true;
            continue;
        }
        i = tag.end;
        if (DROP_WITH_CONTENT.has(tag.name)) {
            changed = true;
            if (!tag.closing) {
                const close = lower.indexOf(`</${tag.name}`, i);
                const gt = close === -1 ? -1 : input.indexOf('>', close);
                i = gt === -1 ? input.length : gt + 1;
            }
            continue;
        }
        if (!ALLOWED_TAGS.has(tag.name)) {
            changed = true;
            continue;
        }
        if (tag.closing) {
            if (!VOID_TAGS.has(tag.name))
                out += `</${tag.name}>`;
            continue;
        }
        const allowed = ALLOWED_ATTRS[tag.name] ?? [];
        let attrs = '';
        for (const [name, value] of tag.attrs) {
            const keep = allowed.includes(name) && (!URL_ATTRS.has(name) || isSafeUrl(value));
            if (keep)
                attrs += ` ${name}="${escapeAttr(value)}"`;
            else
                changed = true;
        }
        out += `<${tag.name}${attrs}>`;
    }
    return { html: out, changed };
}
//# sourceMappingURL=sanitize.js.map